import React, { useState, useEffect } from 'react';
import { Drawer, Box, IconButton, Typography, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { X, Clock, User, FileText, AlertCircle, Plus, Sparkles, Zap, CheckCircle, Trash2, Edit3, Tag, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../../api/client';

const safeParseJson = (val) => {
  if (typeof val !== 'string') return val;
  const trimmed = val.trim();
  if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return val;
  try {
    return JSON.parse(val);
  } catch (e) {
    return val;
  }
};

const extractArray = (val) => {
  const parsed = safeParseJson(val);
  if (Array.isArray(parsed)) return parsed;
  return null;
};

const isQuestionLike = (item) => {
  if (!item || typeof item !== 'object') return false;
  return Boolean(
    item.nameOfQuestion ||
    item.question ||
    item.questionBankId != null ||
    item.choices ||
    item.time_limit != null ||
    (item.type && ['mcq', 'coding', 'concept', 'theoretical', 'subjective', 'audio', 'video'].includes(String(item.type).toLowerCase()))
  );
};

const isQuestionsChange = (fieldName, oldVal, newVal) => {
  const name = String(fieldName || '').toLowerCase();
  if (name.includes('question')) return true;
  const oldArr = extractArray(oldVal);
  const newArr = extractArray(newVal);
  if (oldArr && oldArr.some(isQuestionLike)) return true;
  if (newArr && newArr.some(isQuestionLike)) return true;
  return false;
};

const getQuestionText = (q) => {
  if (!q) return '';
  if (typeof q === 'string') return q;
  return q.nameOfQuestion || q.question || q.title || q.text || q.prompt || 'Untitled question';
};

const getQuestionTypeLabel = (q) => {
  if (!q || typeof q !== 'object') return 'Question';
  const type = String(q.type || q.question_type || '').toLowerCase();
  if (type === 'mcq') return 'MCQ';
  if (type === 'coding') return 'Coding';
  if (type === 'concept' || type === 'theoretical') return 'Theoretical';
  if (type === 'audio') return 'Voice';
  return type ? type.toUpperCase() : 'Question';
};

const getQuestionKey = (q) => {
  if (!q || typeof q !== 'object') return String(q);
  if (q.id != null) return `id_${q.id}`;
  if (q.questionBankId != null) return `qb_${q.questionBankId}`;
  const text = getQuestionText(q).trim().toLowerCase();
  if (text) return `text_${text}`;
  return JSON.stringify(q);
};

const diffQuestions = (oldVal, newVal) => {
  const oldList = extractArray(oldVal) || [];
  const newList = extractArray(newVal) || [];

  const oldMap = new Map();
  oldList.forEach(q => oldMap.set(getQuestionKey(q), q));

  const newMap = new Map();
  newList.forEach(q => newMap.set(getQuestionKey(q), q));

  const added = [];
  const removed = [];
  const modified = [];

  newList.forEach(newQ => {
    const key = getQuestionKey(newQ);
    if (!oldMap.has(key)) {
      added.push(newQ);
    } else {
      const oldQ = oldMap.get(key);
      const changes = [];
      const oldText = getQuestionText(oldQ);
      const newText = getQuestionText(newQ);
      if (oldText !== newText) {
        changes.push({ label: 'Question text', oldVal: oldText, newVal: newText });
      }
      const oldType = getQuestionTypeLabel(oldQ);
      const newType = getQuestionTypeLabel(newQ);
      if (oldType !== newType) {
        changes.push({ label: 'Type', oldVal: oldType, newVal: newType });
      }
      if (oldQ.time_limit !== newQ.time_limit && (oldQ.time_limit != null || newQ.time_limit != null)) {
        changes.push({
          label: 'Time limit',
          oldVal: oldQ.time_limit ? `${oldQ.time_limit} min` : 'None',
          newVal: newQ.time_limit ? `${newQ.time_limit} min` : 'None',
        });
      }
      if (changes.length > 0) {
        modified.push({ question: newQ, changes });
      }
    }
  });

  oldList.forEach(oldQ => {
    const key = getQuestionKey(oldQ);
    if (!newMap.has(key)) {
      removed.push(oldQ);
    }
  });

  return { added, removed, modified, totalOld: oldList.length, totalNew: newList.length };
};

const QuestionItemRow = ({ question, variant, changes }) => {
  const [expanded, setExpanded] = useState(false);
  const type = getQuestionTypeLabel(question);
  const text = getQuestionText(question);

  const isRemoved = variant === 'removed';
  const isAdded = variant === 'added';
  const isModified = variant === 'modified';

  const cardBg = isRemoved ? '#fff1f2' : isAdded ? '#f0fdf4' : '#fffbeb';
  const cardBorder = isRemoved ? '#fecdd3' : isAdded ? '#bbf7d0' : '#fde68a';
  const textColor = isRemoved ? '#9f1239' : isAdded ? '#14532d' : '#78350f';
  const actionColor = isRemoved ? '#e11d48' : isAdded ? '#16a34a' : '#d97706';

  const typeBadgeStyles = {
    MCQ: { bg: '#ede9fe', color: '#6d28d9', border: '#ddd6fe' },
    Coding: { bg: '#e0f2fe', color: '#0369a1', border: '#bae6fd' },
    Theoretical: { bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
    Voice: { bg: '#fce7f3', color: '#be185d', border: '#fbcfe8' },
  }[type] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };

  const rawChoices = Array.isArray(question.choices) ? question.choices : [];
  const timeLimit = question.time_limit;

  return (
    <div style={{
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      borderRadius: '8px',
      padding: '8px 10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    }}>
      {/* Top Meta Row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{
            fontSize: '0.65rem',
            fontWeight: 700,
            padding: '1px 6px',
            borderRadius: '4px',
            background: typeBadgeStyles.bg,
            color: typeBadgeStyles.color,
            border: `1px solid ${typeBadgeStyles.border}`,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          }}>
            {type}
          </span>
          {timeLimit != null && (
            <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '2px' }}>
              ⏱️ {timeLimit} min
            </span>
          )}
        </div>
        {rawChoices.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.7rem',
              color: actionColor,
              fontWeight: 600,
              padding: '0 2px',
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            }}
          >
            <span>{expanded ? 'Hide' : `${rawChoices.length} options`}</span>
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
      </div>

      {/* Question Text */}
      <div style={{
        fontSize: '0.82rem',
        fontWeight: 600,
        color: textColor,
        lineHeight: '1.45',
        textDecoration: isRemoved ? 'line-through' : 'none',
        wordBreak: 'break-word',
      }}>
        {text}
      </div>

      {/* Expandable Choices */}
      {rawChoices.length > 0 && expanded && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '3px',
          marginTop: '4px',
          paddingTop: '6px',
          borderTop: `1px dashed ${cardBorder}`,
        }}>
          {rawChoices.map((c, cIdx) => {
            const choiceText = typeof c === 'object' && c !== null ? (c.text ?? '') : String(c ?? '');
            const isCorrect = typeof c === 'object' && c !== null ? Boolean(c.is_correct) : false;
            return (
              <div key={cIdx} style={{
                fontSize: '0.72rem',
                color: isCorrect ? '#15803d' : '#64748b',
                fontWeight: isCorrect ? 700 : 500,
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}>
                <span style={{ opacity: 0.7, width: '14px' }}>{String.fromCharCode(65 + cIdx)}.</span>
                <span style={{ flex: 1 }}>{choiceText}</span>
                {isCorrect && <Check size={12} color="#16a34a" strokeWidth={3} />}
              </div>
            );
          })}
        </div>
      )}

      {/* Modified properties */}
      {isModified && changes && changes.length > 0 && (
        <div style={{
          fontSize: '0.72rem',
          color: '#92400e',
          marginTop: '4px',
          paddingTop: '4px',
          borderTop: `1px dashed ${cardBorder}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}>
          {changes.map((c, i) => (
            <div key={i}>
              <span style={{ fontWeight: 600 }}>{c.label}:</span>{' '}
              <span style={{ textDecoration: 'line-through', color: '#ef4444' }}>{c.oldVal}</span>{' → '}
              <span style={{ fontWeight: 700, color: '#10b981' }}>{c.newVal}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const QuestionsDiffView = ({ change, t = (k, d) => d?.defaultValue || k }) => {
  const { field, added = [], removed = [], modified = [] } = change;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      width: '100%',
    }}>
      {/* Header bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '6px',
        paddingBottom: '8px',
        borderBottom: '1px solid #e2e8f0',
      }}>
        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#334155' }}>
          {field}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
          {removed.length > 0 && (
            <span style={{
              fontSize: '0.68rem',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: '9999px',
              background: '#fee2e2',
              color: '#ef4444',
              border: '1px solid #fecaca',
            }}>
              −{removed.length} {t('timeline.removed', { defaultValue: 'removed' })}
            </span>
          )}
          {added.length > 0 && (
            <span style={{
              fontSize: '0.68rem',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: '9999px',
              background: '#dcfce7',
              color: '#10b981',
              border: '1px solid #bbf7d0',
            }}>
              +{added.length} {t('timeline.added', { defaultValue: 'added' })}
            </span>
          )}
          {modified.length > 0 && (
            <span style={{
              fontSize: '0.68rem',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: '9999px',
              background: '#fef3c7',
              color: '#d97706',
              border: '1px solid #fde68a',
            }}>
              {modified.length} {t('timeline.updated', { defaultValue: 'updated' })}
            </span>
          )}
        </div>
      </div>

      {/* Removed Questions Section */}
      {removed.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{
            fontSize: '0.72rem',
            fontWeight: 700,
            color: '#e11d48',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}>
            <Trash2 size={13} color="#e11d48" />
            <span>{t('timeline.removedQuestions', { defaultValue: 'Removed Questions' })} ({removed.length})</span>
          </div>
          {removed.map((q, idx) => (
            <QuestionItemRow key={`rem-${idx}`} question={q} variant="removed" />
          ))}
        </div>
      )}

      {/* Added Questions Section */}
      {added.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{
            fontSize: '0.72rem',
            fontWeight: 700,
            color: '#16a34a',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}>
            <Plus size={13} color="#16a34a" strokeWidth={3} />
            <span>{t('timeline.addedQuestions', { defaultValue: 'Added Questions' })} ({added.length})</span>
          </div>
          {added.map((q, idx) => (
            <QuestionItemRow key={`add-${idx}`} question={q} variant="added" />
          ))}
        </div>
      )}

      {/* Modified Questions Section */}
      {modified.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{
            fontSize: '0.72rem',
            fontWeight: 700,
            color: '#d97706',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}>
            <Edit3 size={13} color="#d97706" />
            <span>{t('timeline.modifiedQuestions', { defaultValue: 'Updated Questions' })} ({modified.length})</span>
          </div>
          {modified.map((item, idx) => (
            <QuestionItemRow key={`mod-${idx}`} question={item.question} changes={item.changes} variant="modified" />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Timeline Component - Beautiful & Modern
 * 
 * A stunning timeline component with connected cards, vibrant gradients, and smooth animations.
 * Features glassmorphism effects and a premium aesthetic.
 * 
 * @param {boolean} open - Controls drawer visibility
 * @param {function} onClose - Callback when drawer is closed
 * @param {string} apiEndpoint - API endpoint to fetch timeline data from
 * @param {object} apiParams - Additional query parameters for the API call
 * @param {string} title - Title displayed in the drawer header (default: "Timeline")
 * @param {string} pageName - Name of the page using this timeline (displayed in header)
 * @param {number} width - Drawer width in pixels (default: 450)
 * @param {object} candidateInfo - Object containing candidateName and sessionId to display in header
 */
const Timeline = ({
  open,
  onClose,
  apiEndpoint,
  apiParams = {},
  title = 'Timeline',
  pageName = '',
  width = 450,
  candidateInfo = null,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isRTL = document.documentElement.dir === 'rtl' || i18n.language === 'ar';
  
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  // Fetch timeline data
  const fetchTimelineData = async (page = 1, append = false) => {
    if (!apiEndpoint) {
      setError(t('timeline.apiEndpointRequired'));
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const params = {
        ...apiParams,
        page: page,
        page_size: pagination.page_size || 20,
      };

      const response = await api.get(apiEndpoint, { params });
      const data = response.data;

      if (append) {
        setTimelineData(prev => [...prev, ...(data.results || [])]);
      } else {
        setTimelineData(data.results || []);
      }

      setPagination(data.pagination || {
        current_page: 1,
        page_size: 20,
        total_count: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
      });
    } catch (err) {
      console.error('Error fetching timeline data:', err);
      setError(err.response?.data?.detail || t('timeline.failedToLoad'));
      if (!append) {
        setTimelineData([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (pagination.has_next && !loadingMore) {
      fetchTimelineData(pagination.current_page + 1, true);
    }
  };

  useEffect(() => {
    if (open && apiEndpoint) {
      fetchTimelineData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apiEndpoint]);

  useEffect(() => {
    if (!open) {
      setTimelineData([]);
      setError(null);
      setPagination({
        current_page: 1,
        page_size: 20,
        total_count: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
      });
    }
  }, [open]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatHumanReadableDescription = (item) => {
    const actor = item.actor_name || item.actor_email || t('timeline.unknown');
    const cleanActor = actor.split(' ').filter((val, idx, arr) => val !== arr[idx - 1]).join(' ');
    const action = item.action || t('timeline.actions.performedAction');
    let entityType = item.entity_type || item.module || 'item';
    entityType = entityType.charAt(0).toUpperCase() + entityType.slice(1).toLowerCase();

    let actionText = action.charAt(0).toUpperCase() + action.slice(1).toLowerCase();
    if (action.toLowerCase() === 'created') {
      actionText = t('timeline.actions.created');
    } else if (action.toLowerCase() === 'updated') {
      actionText = t('timeline.actions.updated');
    } else if (action.toLowerCase() === 'deleted') {
      actionText = t('timeline.actions.deleted');
    } else if (action.toLowerCase() === 'tag_added') {
      actionText = t('timeline.actions.tagAdded', { defaultValue: 'Tag added' });
    } else if (action.toLowerCase() === 'tag_removed') {
      actionText = t('timeline.actions.tagRemoved', { defaultValue: 'Tag removed' });
    }

    return { actor: cleanActor, actionText, entityType, entityName: item.entity_name };
  };

  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  const formatHumanReadableChanges = (changes) => {
    if (!changes) return [];

    const isIsoDateString = (str) => {
      if (typeof str !== 'string') return false;
      return /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(str.trim());
    };

    const formatHumanDate = (str) => {
      if (!str) return '—';
      try {
        const trimmed = String(str).trim();
        // Date only (YYYY-MM-DD)
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
          const [year, month, day] = trimmed.split('-').map(Number);
          const d = new Date(year, month - 1, day);
          if (!isNaN(d.getTime())) {
            return d.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            });
          }
        }
        // Full timestamp with time
        const d = new Date(trimmed);
        if (!isNaN(d.getTime())) {
          return d.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
        }
      } catch (e) {
        // fallback
      }
      return str;
    };

    const formatValue = (val) => {
      if (val === null || val === undefined) return 'None';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      if (typeof val === 'number') return String(val);
      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (isIsoDateString(trimmed)) {
          return formatHumanDate(trimmed);
        }
        return trimmed;
      }
      if (typeof val === 'object') {
        try {
          if (Array.isArray(val)) {
            if (val.length === 0) return 'None';
            return val.map(v => typeof v === 'object' ? formatValue(v) : String(v)).join(', ');
          }
          const entries = Object.entries(val);
          if (entries.length === 0) return 'None';
          const isSimple = entries.every(([_, v]) => typeof v !== 'object' || v === null);
          if (isSimple) {
            return entries.map(([k, v]) => `${formatFieldName(k)}: ${v === null ? 'None' : formatValue(v)}`).join(', ');
          }
          return JSON.stringify(val);
        } catch (e) {
          return String(val);
        }
      }
      return String(val);
    };

    const processFieldChange = (fieldName, change) => {
      if (change && typeof change === 'object') {
        // If it's a direct change with 'old' or 'new'
        if ('old' in change || 'new' in change) {
          const oldValue = change.old;
          const newValue = change.new;

          // Check if this is a questions change
          if (isQuestionsChange(fieldName, oldValue, newValue)) {
            const diff = diffQuestions(oldValue, newValue);
            if (diff.added.length > 0 || diff.removed.length > 0 || diff.modified.length > 0) {
              return [{
                field: fieldName,
                type: 'questions_diff',
                isQuestions: true,
                ...diff,
              }];
            }
            return [];
          }

          if (oldValue !== undefined && newValue !== undefined) {
            const formattedOld = formatValue(oldValue);
            const formattedNew = formatValue(newValue);

            // If formatting produces identical strings (e.g. sub-second timestamp noise)
            if (formattedOld === formattedNew) {
              return [{
                field: fieldName,
                type: 'changed',
                newValue: formattedNew,
              }];
            }

            return [{
              field: fieldName,
              type: 'updated',
              oldValue: formattedOld,
              newValue: formattedNew,
            }];
          } else if (oldValue === undefined && newValue !== undefined) {
            return [{
              field: fieldName,
              type: 'added',
              newValue: formatValue(newValue),
            }];
          } else if (oldValue !== undefined && newValue === undefined) {
            return [{
              field: fieldName,
              type: 'removed',
              oldValue: formatValue(oldValue),
            }];
          }
        }

        // If it does not have 'old' or 'new', check if it contains nested changes
        if (!Array.isArray(change)) {
          const subKeys = Object.keys(change);
          const hasNestedChanges = subKeys.some(k => {
            const val = change[k];
            return val && typeof val === 'object' && ('old' in val || 'new' in val);
          });

          if (hasNestedChanges) {
            let nestedResults = [];
            subKeys.forEach(k => {
              const formattedSubFieldName = `${fieldName} → ${formatFieldName(k)}`;
              nestedResults = nestedResults.concat(processFieldChange(formattedSubFieldName, change[k]));
            });
            return nestedResults;
          }
        }
      }

      // Default fallback
      if (change !== undefined) {
        if (isQuestionsChange(fieldName, null, change)) {
          const diff = diffQuestions(null, change);
          if (diff.added.length > 0) {
            return [{
              field: fieldName,
              type: 'questions_diff',
              isQuestions: true,
              ...diff,
            }];
          }
        }
        return [{
          field: fieldName,
          type: 'changed',
          newValue: formatValue(change),
        }];
      }

      return [];
    };

    try {
      let parsedChanges = changes;
      if (typeof changes === 'string') {
        parsedChanges = JSON.parse(changes);
      }

      if (typeof parsedChanges !== 'object' || parsedChanges === null) {
        return [];
      }

      const rawKeys = Object.keys(parsedChanges);
      // If there are other substantive changes, skip internal auto-timestamp noise (updated_at)
      const hasSubstantiveChanges = rawKeys.some(k => {
        const lower = k.toLowerCase().replace(/_/g, '');
        return lower !== 'updatedat' && lower !== 'createdat';
      });

      let formattedChanges = [];
      rawKeys.forEach((field) => {
        const lower = field.toLowerCase().replace(/_/g, '');
        if (hasSubstantiveChanges && (lower === 'updatedat' || lower === 'createdat')) {
          return;
        }
        const change = parsedChanges[field];
        const fieldName = formatFieldName(field);
        formattedChanges = formattedChanges.concat(processFieldChange(fieldName, change));
      });

      return formattedChanges;
    } catch (error) {
      console.error('Error parsing changes:', error);
      return [];
    }
  };

  const getActionTheme = (action) => {
    switch (action?.toLowerCase()) {
      case 'created':
        return {
          gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          icon: CheckCircle,
          iconColor: '#667eea',
          dotColor: '#667eea',
          accentColor: '#667eea',
          borderColor: 'rgba(102, 126, 234, 0.3)',
          shadowColor: 'rgba(102, 126, 234, 0.3)',
        };
      case 'updated':
        return {
          gradient: 'linear-gradient(135deg, #9862b7ff 0%, #764ba2 100%)',
          icon: Edit3,
          iconColor: '#667eea',
          dotColor: '#667eea',
          accentColor: '#667eea',
          borderColor: 'rgba(102, 126, 234, 0.3)',
          shadowColor: 'rgba(251, 251, 253, 0.3)',
        };
      case 'deleted':
        return {
          gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          icon: Trash2,
          iconColor: '#fa709a',
          dotColor: '#fa709a',
          accentColor: '#fa709a',
          borderColor: 'rgba(250, 112, 154, 0.3)',
          shadowColor: 'rgba(250, 112, 154, 0.3)',
        };
      case 'tag_added':
        return {
          gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          icon: Tag,
          iconColor: '#11998e',
          dotColor: '#11998e',
          accentColor: '#11998e',
          borderColor: 'rgba(17, 153, 142, 0.3)',
          shadowColor: 'rgba(17, 153, 142, 0.3)',
        };
      case 'tag_removed':
        return {
          gradient: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
          icon: Tag,
          iconColor: '#eb3349',
          dotColor: '#eb3349',
          accentColor: '#eb3349',
          borderColor: 'rgba(235, 51, 73, 0.3)',
          shadowColor: 'rgba(235, 51, 73, 0.3)',
        };
      default:
        return {
          gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          icon: Sparkles,
          iconColor: '#4facfe',
          dotColor: '#4facfe',
          accentColor: '#4facfe',
          borderColor: 'rgba(79, 172, 254, 0.3)',
          shadowColor: 'rgba(79, 172, 254, 0.3)',
        };
    }
  };

  return (
    <Drawer
      anchor={isRTL ? 'left' : 'right'}
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : width,
          maxWidth: isMobile ? '100%' : width,
          boxSizing: 'border-box',
          background: '#ffffff',
          backdropFilter: 'blur(20px)',
          [isRTL ? 'borderRight' : 'borderLeft']: 'none',
          boxShadow: isRTL 
            ? '10px 0 40px rgba(0, 0, 0, 0.1)' 
            : '-10px 0 40px rgba(0, 0, 0, 0.1)',
          zIndex: 1400,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderBottom: 'none',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
        }}
      >
        {pageName && (
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: 'rgba(255, 255, 255, 0.9)',
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Sparkles size={14} />
              {pageName}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                marginBottom: candidateInfo ? '8px' : 0,
              }}
            >
              <Clock size={28} strokeWidth={2.5} />
              {title || t('timeline.title')}
            </Typography>
            {candidateInfo && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  mt: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'rgba(255, 255, 255, 0.95)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <User size={16} />
                  {candidateInfo.candidateName || '—'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontFamily: 'monospace',
                  }}
                >
                  <FileText size={14} />
                  Session ID: {candidateInfo.sessionId || '—'}
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.25)',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
        }}
      >
        {loading && timelineData.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              gap: 3,
            }}
          >
            <CircularProgress
              size={50}
              thickness={4}
              sx={{
                color: '#667eea',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: '#667eea',
                fontWeight: 600,
                fontSize: '1rem',
              }}
            >
              {t('timeline.loading')}
            </Typography>
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              gap: 3,
              p: 4,
            }}
          >
            <AlertCircle size={60} style={{ color: '#f5576c' }} />
            <Typography
              variant="body1"
              sx={{
                color: '#f5576c',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {error}
            </Typography>
            <button
              onClick={() => fetchTimelineData(pagination.current_page)}
              style={{
                padding: '12px 28px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }}
            >
              {t('timeline.retry')}
            </button>
          </Box>
        ) : timelineData.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              gap: 2,
            }}
          >
            <FileText size={60} style={{ color: '#cbd5e1' }} />
            <Typography
              variant="body1"
              sx={{
                color: '#94a3b8',
                fontWeight: 500,
              }}
            >
              {t('timeline.noEvents')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Timeline Items */}
            <div style={{ position: 'relative' }}>
              {timelineData.map((item, index) => {
                const theme = getActionTheme(item.action);
                const IconComponent = theme.icon;
                const isLast = index === timelineData.length - 1;

                return (
                  <div
                    key={item.id || index}
                    style={{
                      position: 'relative',
                      marginBottom: isLast ? '0' : '0px',
                      [isRTL ? 'paddingRight' : 'paddingLeft']: '40px',
                    }}
                  >
                    {/* Connecting Line */}
                    {!isLast && (
                      <div
                        style={{
                          position: 'absolute',
                          [isRTL ? 'right' : 'left']: '15px',
                          top: '40px',
                          bottom: '-8px',
                          width: '3px',
                          background: theme.gradient,
                          borderRadius: '10px',
                          opacity: 0.4,
                        }}
                      />
                    )}

                    {/* Timeline Dot with Glow */}
                    <div
                      style={{
                        position: 'absolute',
                        [isRTL ? 'right' : 'left']: '0',
                        top: '20px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: theme.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 0 20px ${theme.shadowColor}, 0 0 40px ${theme.shadowColor}`,
                        zIndex: 10,
                        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                      }}
                    >
                      <IconComponent size={16} color="white" strokeWidth={2.5} />
                    </div>

                    {/* Content Card */}
                    <div
                      style={{
                        background: '#ffffff',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '16px',
                        padding: '20px',
                        marginBottom: '16px',
                        border: `2px solid ${theme.borderColor}`,
                        boxShadow: `0 8px 32px ${theme.shadowColor}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                        e.currentTarget.style.boxShadow = `0 12px 48px ${theme.shadowColor}`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = `0 8px 32px ${theme.shadowColor}`;
                      }}
                    >
                      {/* Gradient Accent Bar */}
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: theme.gradient,
                        }}
                      />

                      {/* Header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: '16px',
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          color: '#475569',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                        }}>
                          <Clock size={18} />
                          <span>{formatDate(item.occurred_at)}</span>
                        </div>
                      </div>

                      {/* Description */}
                      <div style={{ marginBottom: '16px' }}>
                        {(() => {
                          if (item.description) {
                            return (
                              <div style={{
                                fontSize: '0.95rem',
                                lineHeight: '1.6',
                                color: '#1e293b',
                              }}>
                                <span style={{
                                  fontWeight: 600,
                                  color: theme.accentColor,
                                }}>
                                  {item.description}
                                </span>
                              </div>
                            );
                          }
                          const { actor, actionText, entityType, entityName } = formatHumanReadableDescription(item);

                          return (
                            <div style={{
                              fontSize: '0.95rem',
                              lineHeight: '1.6',
                              color: '#1e293b',
                            }}>
                              <span style={{
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                              }}>
                                {actor}
                              </span>
                              {' '}
                              <span style={{
                                fontWeight: 600,
                                color: theme.accentColor,
                              }}>
                                {actionText}
                              </span>
                              {' '}
                              <span style={{ fontWeight: 500, color: '#475569' }}>
                                {entityType}
                              </span>
                              {entityName && entityName !== entityType && (
                                <span style={{
                                  color: '#64748b',
                                  fontSize: '0.85rem',
                                  [isRTL ? 'marginRight' : 'marginLeft']: '4px',
                                }}>
                                  ({entityName})
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Changes */}
                      {item.changes && (() => {
                        const formattedChanges = formatHumanReadableChanges(item.changes);
                        return formattedChanges.length > 0 ? (
                          <div style={{
                            background: '#f8fafc',
                            borderRadius: '12px',
                            padding: '14px',
                            border: `1px solid ${theme.borderColor}`,
                          }}>
                            <div style={{
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              color: theme.accentColor,
                              marginBottom: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}>
                              <Zap size={14} />
                              {t('timeline.changes')}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {formattedChanges.map((change, idx) => (
                                <div key={idx} style={{ fontSize: '0.85rem', lineHeight: '1.5' }}>
                                  {change.type === 'questions_diff' && (
                                    <QuestionsDiffView change={change} t={t} />
                                  )}
                                  {change.type === 'updated' && (
                                    <div>
                                      <span style={{ fontWeight: 600, color: '#475569' }}>
                                        {change.field}:
                                      </span>
                                      {' '}
                                      <span style={{
                                        color: '#ef4444',
                                        textDecoration: 'line-through',
                                        opacity: 0.7,
                                      }}>
                                        {change.oldValue}
                                      </span>
                                      {' → '}
                                      <span style={{
                                        fontWeight: 600,
                                        color: '#10b981',
                                      }}>
                                        {change.newValue}
                                      </span>
                                    </div>
                                  )}
                                  {change.type === 'added' && (
                                    <div>
                                      <span style={{ fontWeight: 600, color: '#475569' }}>
                                        {change.field}:
                                      </span>
                                      {' '}
                                      <span style={{
                                        fontWeight: 600,
                                        color: '#10b981',
                                      }}>
                                        {change.newValue}
                                      </span>
                                      {' '}
                                      <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                        ({t('timeline.new')})
                                      </span>
                                    </div>
                                  )}
                                  {change.type === 'removed' && (
                                    <div>
                                      <span style={{ fontWeight: 600, color: '#475569' }}>
                                        {change.field}:
                                      </span>
                                      {' '}
                                      <span style={{
                                        color: '#ef4444',
                                        textDecoration: 'line-through',
                                      }}>
                                        {change.oldValue}
                                      </span>
                                      {' '}
                                      <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                        ({t('timeline.removed')})
                                      </span>
                                    </div>
                                  )}
                                  {change.type === 'changed' && (
                                    <div>
                                      <span style={{ fontWeight: 600, color: '#475569' }}>
                                        {change.field}:
                                      </span>
                                      {' '}
                                      <span style={{
                                        fontWeight: 600,
                                        color: '#10b981',
                                      }}>
                                        {change.newValue}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More Button */}
            {timelineData.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                {loadingMore ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 2,
                      py: 3,
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: '#667eea' }} />
                    <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 600 }}>
                      {t('timeline.loadingMore')}
                    </Typography>
                  </Box>
                ) : pagination.has_next ? (
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '14px',
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 28px rgba(102, 126, 234, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.4)';
                    }}
                  >
                    <Plus size={20} strokeWidth={2.5} />
                    {t('timeline.loadMore')}
                  </button>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: '#94a3b8',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                  }}>
                    {t('timeline.endReached')}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Box>

      {/* Add CSS animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.8;
            }
          }
        `}
      </style>
    </Drawer>
  );
};

export default Timeline;
